import React, { useState, useEffect } from 'react';
import { Document, Matter, Project, DocumentFolder, Goal, Client, db } from '../../lib/db';
import { X, Folder, FolderOpen, Plus, FileText, Target, Users, Briefcase } from 'lucide-react';
import { toast } from 'sonner';
import { useTimer } from '../../contexts/TimerContext';
import FolderManager from './FolderManager';
import { TemplateModal } from '../templates/TemplateModal';

interface DocumentFilingPanelProps {
  document: Document;
  matters: Matter[];
  projects: Project[];
  clients: Client[];
  dbUserId: string;
  onFiled: () => void;
  onClose: () => void;
}

function DocumentFilingPanel({ 
  document, 
  matters, 
  projects, 
  clients: propsClients,
  dbUserId, 
  onFiled, 
  onClose 
}: DocumentFilingPanelProps) {
  const { timerState, stopTimer } = useTimer();
  const [selectedMatterId, setSelectedMatterId] = useState<string | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [folders, setFolders] = useState<DocumentFolder[]>([]);
  const [notes, setNotes] = useState('');
  const [showFolderManager, setShowFolderManager] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [deliverables, setDeliverables] = useState<Goal[]>([]);
  const [selectedDeliverableId, setSelectedDeliverableId] = useState<string | null>(null);
  const [filing, setFiling] = useState(false);
  const [suggestedFilename, setSuggestedFilename] = useState(document.suggested_filename || document.filename);
  const [clients, setClients] = useState<Client[]>(propsClients);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  
  // Forms state
  const [showNewMatter, setShowNewMatter] = useState(false);
  const [newMatterTitle, setNewMatterTitle] = useState('');
  const [newMatterDescription, setNewMatterDescription] = useState('');
  
  const [showNewClient, setShowNewClient] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [newClientEmail, setNewClientEmail] = useState('');
  const [newClientPhone, setNewClientPhone] = useState('');
  
  const [showNewProject, setShowNewProject] = useState(false);
  const [newProjectTitle, setNewProjectTitle] = useState('');
  const [newProjectDescription, setNewProjectDescription] = useState('');

  useEffect(() => {
    loadFolders();
  }, [selectedMatterId, selectedProjectId]);

  useEffect(() => {
    loadDeliverables();
  }, [selectedMatterId, selectedProjectId]);
  
  useEffect(() => {
    setClients(propsClients);
  }, [propsClients]);

  const loadFolders = async () => {
    if (!dbUserId) return;
    
    try {
      let folderList: DocumentFolder[] = [];
      
      if (selectedMatterId) {
        folderList = await db.documentFolders.findByMatterId(selectedMatterId, dbUserId);
      } else if (selectedProjectId) {
        folderList = await db.documentFolders.findByProjectId(selectedProjectId, dbUserId);
      }
      
      setFolders(folderList);
    } catch (error) {
      console.error('Error loading folders:', error);
    }
  };

  const loadDeliverables = async () => {
    if (!dbUserId || (!selectedMatterId && !selectedProjectId)) return;
    
    try {
      let goals: Goal[] = [];
      
      if (selectedMatterId) {
        const allGoals = await db.goals.findByUserId(dbUserId);
        goals = allGoals.filter(g => g.matter_id === selectedMatterId);
      } else if (selectedProjectId) {
        const allGoals = await db.goals.findByUserId(dbUserId);
        goals = allGoals.filter(g => g.project_id === selectedProjectId);
      }
      
      setDeliverables(goals);
    } catch (error) {
      console.error('Error loading deliverables:', error);
    }
  };
  
  
  const handleCreateClient = async () => {
    if (!newClientName.trim()) {
      toast.error('Please enter a client name');
      return;
    }
    
    try {
      const client = await db.clients.create({
        user_id: dbUserId,
        name: newClientName.trim(),
        email: newClientEmail || undefined,
        phone: newClientPhone || undefined,
        position: 0
      });
      
      // Reload clients by triggering parent update
      onFiled();
      setSelectedClientId(client.id);
      setShowNewClient(false);
      setNewClientName('');
      setNewClientEmail('');
      setNewClientPhone('');
      toast.success('Client created successfully');
    } catch (error) {
      console.error('Error creating client:', error);
      toast.error('Failed to create client');
    }
  };
  
  const handleCreateMatter = async () => {
    if (!newMatterTitle.trim()) {
      toast.error('Please enter a matter title');
      return;
    }
    
    try {
      const matter = await db.matters.create({
        user_id: dbUserId,
        title: newMatterTitle.trim(),
        description: newMatterDescription || undefined,
        project_id: selectedProjectId || undefined,
        client_id: selectedClientId || undefined,
        status: 'Active',
        position: 0
      });
      
      onFiled(); // This will reload matters
      setSelectedMatterId(matter.id);
      setShowNewMatter(false);
      setNewMatterTitle('');
      setNewMatterDescription('');
      toast.success('Matter created successfully');
    } catch (error) {
      console.error('Error creating matter:', error);
      toast.error('Failed to create matter');
    }
  };
  
  const handleCreateProject = async () => {
    if (!newProjectTitle.trim()) {
      toast.error('Please enter a project title');
      return;
    }
    
    try {
      const project = await db.projects.create({
        user_id: dbUserId,
        title: newProjectTitle.trim(),
        description: newProjectDescription || undefined,
        position: 0,
        status: 'Active'
      });
      
      onFiled(); // This will reload projects
      setSelectedProjectId(project.id);
      setShowNewProject(false);
      setNewProjectTitle('');
      setNewProjectDescription('');
      toast.success('Project created successfully');
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error('Failed to create project');
    }
  };

  const handleFileDocument = async () => {
    if (!selectedMatterId && !selectedProjectId) {
      toast.error('Please select a matter or project');
      return;
    }
    
    setFiling(true);
    
    try {
      // Update document filename and status
      await db.documents.update(document.id, dbUserId, {
        filename: suggestedFilename,
        status: 'filed'
      });
      
      // Create document association
      await db.documentAssociations.create({
        document_id: document.id,
        user_id: dbUserId,
        matter_id: selectedMatterId,
        project_id: selectedProjectId,
        folder_id: selectedFolderId,
        goal_id: selectedDeliverableId,
        notes
      });
      
      // End review session if timer is running
      if (timerState.isRunning) {
        const activeSession = await db.documentReviewSessions.findActive(dbUserId);
        if (activeSession) {
          await db.documentReviewSessions.endSession(
            activeSession.id,
            dbUserId,
            timerState.timeEntryId,
            'filed'
          );
        }
        
        // Stop the timer
        const wasCompleted = await stopTimer(notes || 'Document filed');
        
        // If a matter or project was selected, associate the time entry
        if (wasCompleted && timerState.timeEntryId) {
          // The time will be associated with the task created for document review
          // which can be linked to the matter/project through reporting
        }
      }
      
      toast.success('Document filed successfully. You can now process it from the Processing tab.');
      onFiled();
    } catch (error) {
      console.error('Error filing document:', error);
      toast.error('Failed to file document');
    } finally {
      setFiling(false);
    }
  };

  const handleCreateDeliverable = async () => {
    setShowTemplateModal(true);
  };

  const onDeliverableCreated = async () => {
    // Reload deliverables
    await loadDeliverables();
    setShowTemplateModal(false);
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">File Document</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
      
      {/* Filing Options */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
        {/* Filename */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Filename
          </label>
          <input
            type="text"
            value={suggestedFilename}
            onChange={(e) => setSuggestedFilename(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="Enter filename..."
          />
          <p className="mt-1 text-xs text-gray-500">
            Original: {document.original_filename}
          </p>
        </div>
        
        {/* Client Selection */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Client (Optional)
            </label>
            <button
              onClick={() => setShowNewClient(true)}
              className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center"
            >
              <Plus className="w-3 h-3 mr-1" />
              New Client
            </button>
          </div>
          {showNewClient ? (
            <div className="space-y-2 p-3 bg-gray-50 rounded-md">
              <input
                type="text"
                placeholder="Client name"
                value={newClientName}
                onChange={(e) => setNewClientName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <input
                type="email"
                placeholder="Email (optional)"
                value={newClientEmail}
                onChange={(e) => setNewClientEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <input
                type="tel"
                placeholder="Phone (optional)"
                value={newClientPhone}
                onChange={(e) => setNewClientPhone(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => {
                    setShowNewClient(false);
                    setNewClientName('');
                    setNewClientEmail('');
                    setNewClientPhone('');
                  }}
                  className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateClient}
                  className="px-3 py-1 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                >
                  Create
                </button>
              </div>
            </div>
          ) : (
            <select
              value={selectedClientId || ''}
              onChange={(e) => setSelectedClientId(e.target.value || null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">None</option>
              {clients
                .filter(c => !c.is_archived)
                .map(client => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
            </select>
          )}
        </div>
        
        {/* Project Selection */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Project (Optional)
            </label>
            <button
              onClick={() => setShowNewProject(true)}
              className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center"
            >
              <Plus className="w-3 h-3 mr-1" />
              New Project
            </button>
          </div>
          {showNewProject ? (
            <div className="space-y-2 p-3 bg-gray-50 rounded-md">
              <input
                type="text"
                placeholder="Project title"
                value={newProjectTitle}
                onChange={(e) => setNewProjectTitle(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <textarea
                placeholder="Description (optional)"
                value={newProjectDescription}
                onChange={(e) => setNewProjectDescription(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => {
                    setShowNewProject(false);
                    setNewProjectTitle('');
                    setNewProjectDescription('');
                  }}
                  className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateProject}
                  className="px-3 py-1 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                >
                  Create
                </button>
              </div>
            </div>
          ) : (
            <select
              value={selectedProjectId || ''}
              onChange={(e) => {
                setSelectedProjectId(e.target.value || null);
                setSelectedMatterId(null);
                setSelectedFolderId(null);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">None</option>
              {projects
                .filter(p => p.status !== 'Archived')
                .map(project => (
                  <option key={project.id} value={project.id}>
                    {project.title}
                  </option>
                ))}
            </select>
          )}
        </div>
        
        {/* Matter Selection */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Matter {!selectedProjectId && !selectedMatterId && '(Required)'}
            </label>
            <button
              onClick={() => setShowNewMatter(true)}
              className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center"
            >
              <Plus className="w-3 h-3 mr-1" />
              New Matter
            </button>
          </div>
          {showNewMatter ? (
            <div className="space-y-2 p-3 bg-gray-50 rounded-md">
              <input
                type="text"
                placeholder="Matter title"
                value={newMatterTitle}
                onChange={(e) => setNewMatterTitle(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <textarea
                placeholder="Description (optional)"
                value={newMatterDescription}
                onChange={(e) => setNewMatterDescription(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => {
                    setShowNewMatter(false);
                    setNewMatterTitle('');
                    setNewMatterDescription('');
                  }}
                  className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateMatter}
                  className="px-3 py-1 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                >
                  Create
                </button>
              </div>
            </div>
          ) : (
            <select
              value={selectedMatterId || ''}
              onChange={(e) => {
                setSelectedMatterId(e.target.value || null);
                setSelectedProjectId(null);
                setSelectedFolderId(null);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">None</option>
              {matters
                .filter(m => !m.is_archived && (!selectedProjectId || m.project_id === selectedProjectId))
                .map(matter => (
                  <option key={matter.id} value={matter.id}>
                    {matter.title}
                  </option>
                ))}
            </select>
          )}
        </div>
        
        {/* Folder Selection */}
        {(selectedMatterId || selectedProjectId) && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Folder (Optional)
              </label>
              <button
                onClick={() => setShowFolderManager(true)}
                className="text-sm text-indigo-600 hover:text-indigo-800"
              >
                Manage Folders
              </button>
            </div>
            <select
              value={selectedFolderId || ''}
              onChange={(e) => setSelectedFolderId(e.target.value || null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Root</option>
              {folders.map(folder => (
                <option key={folder.id} value={folder.id}>
                  {folder.name}
                </option>
              ))}
            </select>
          </div>
        )}
        
        {/* Deliverable Selection */}
        {(selectedMatterId || selectedProjectId) && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Associate with Deliverable (Optional)
              </label>
              <button
                onClick={handleCreateDeliverable}
                className="text-sm text-indigo-600 hover:text-indigo-800"
              >
                New Deliverable
              </button>
            </div>
            <select
              value={selectedDeliverableId || ''}
              onChange={(e) => setSelectedDeliverableId(e.target.value || null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">None</option>
              {deliverables
                .filter(d => d.status !== 'Archived' && d.status !== 'Complete')
                .map(deliverable => (
                  <option key={deliverable.id} value={deliverable.id}>
                    {deliverable.title}
                  </option>
                ))}
            </select>
          </div>
        )}
        
        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Notes (Optional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="Add any notes about this document..."
          />
        </div>
      </div>
      
      {/* Footer */}
      <div className="p-4 border-t border-gray-200 bg-white flex-shrink-0">
        <button
          onClick={handleFileDocument}
          disabled={!selectedMatterId && !selectedProjectId || filing}
          className={`w-full px-4 py-2 rounded-md font-medium transition-colors ${
            !selectedMatterId && !selectedProjectId || filing
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-indigo-600 text-white hover:bg-indigo-700'
          }`}
        >
          {filing ? 'Filing...' : 'File Document'}
        </button>
      </div>
      
      {/* Folder Manager Modal */}
      {showFolderManager && (selectedMatterId || selectedProjectId) && (
        <FolderManager
          matterId={selectedMatterId}
          projectId={selectedProjectId}
          dbUserId={dbUserId}
          onClose={() => {
            setShowFolderManager(false);
            loadFolders();
          }}
        />
      )}
      
      {/* Create Deliverable Modal */}
      {showTemplateModal && (
        <TemplateModal
          isOpen={true}
          onClose={() => setShowTemplateModal(false)}
          onSuccess={onDeliverableCreated}
        />
      )}
    </div>
  );
}

export default DocumentFilingPanel;